// Alphabet and pronunciation data for supported target languages
import type { LanguageAlphabet } from "./dialog-data";

// Get the base language code (e.g., "pt" from "pt-PT")
export function getBaseLanguageCode(code: string): string {
  return code.split("-")[0]!;
}

// Spanish alphabet
const spanishAlphabet: LanguageAlphabet = {
  language: "Spanish",
  sections: [
    {
      title: "Vowels",
      description: "Spanish has 5 pure vowel sounds - simpler than English",
      letters: [
        {
          letter: "A",
          name: "a",
          sound: "Open 'ah' as in 'father'",
          examples: [
            { word: "agua", translation: "water", pronunciation: "AH-gwah" },
            { word: "casa", translation: "house", pronunciation: "KAH-sah" },
          ],
        },
        {
          letter: "E",
          name: "e",
          sound: "'eh' as in 'bed'",
          examples: [
            { word: "leche", translation: "milk", pronunciation: "LEH-cheh" },
            { word: "verde", translation: "green", pronunciation: "BEHR-deh" },
          ],
        },
        {
          letter: "I",
          name: "i",
          sound: "'ee' as in 'machine'",
          examples: [
            { word: "libro", translation: "book", pronunciation: "LEE-broh" },
            { word: "hijo", translation: "son", pronunciation: "EE-hoh" },
          ],
        },
        {
          letter: "O",
          name: "o",
          sound: "'oh' as in 'go' but shorter",
          examples: [
            { word: "ojo", translation: "eye", pronunciation: "OH-hoh" },
            { word: "como", translation: "how/like", pronunciation: "KOH-moh" },
          ],
        },
        {
          letter: "U",
          name: "u",
          sound: "'oo' as in 'moon'",
          examples: [
            { word: "uno", translation: "one", pronunciation: "OO-noh" },
            { word: "luna", translation: "moon", pronunciation: "LOO-nah" },
          ],
        },
      ],
    },
    {
      title: "Special Consonants",
      description: "Consonants that differ from English pronunciation",
      letters: [
        {
          letter: "J",
          name: "jota",
          sound: "Throaty 'h' sound (like German 'ch')",
          examples: [
            { word: "jugo", translation: "juice", pronunciation: "HOO-goh" },
            { word: "rojo", translation: "red", pronunciation: "ROH-hoh" },
          ],
        },
        {
          letter: "LL",
          name: "elle/doble ele",
          sound: "'y' as in 'yes' (varies by region)",
          examples: [
            { word: "llamar", translation: "to call", pronunciation: "yah-MAHR" },
            { word: "calle", translation: "street", pronunciation: "KAH-yeh" },
          ],
          notes: "In Argentina/Uruguay, pronounced like 'sh' in 'ship'",
        },
        {
          letter: "Ñ",
          name: "eñe",
          sound: "'ny' as in 'canyon'",
          examples: [
            { word: "niño", translation: "child", pronunciation: "NEE-nyoh" },
            { word: "año", translation: "year", pronunciation: "AH-nyoh" },
          ],
        },
        {
          letter: "RR",
          name: "erre doble",
          sound: "Strongly trilled 'r'",
          examples: [
            { word: "perro", translation: "dog", pronunciation: "PEH-rroh" },
            { word: "carro", translation: "car", pronunciation: "KAH-rroh" },
          ],
        },
        {
          letter: "Z",
          name: "zeta",
          sound: "'th' in Spain, 's' in Latin America",
          examples: [
            { word: "zapato", translation: "shoe", pronunciation: "thah-PAH-toh / sah-PAH-toh" },
            { word: "azul", translation: "blue", pronunciation: "ah-THOOL / ah-SOOL" },
          ],
        },
      ],
    },
  ],
};

// French alphabet
const frenchAlphabet: LanguageAlphabet = {
  language: "French",
  sections: [
    {
      title: "Vowels",
      description: "French has many vowel sounds, including nasal vowels",
      letters: [
        {
          letter: "A",
          name: "a",
          sound: "'ah' as in 'father'",
          examples: [
            { word: "chat", translation: "cat", pronunciation: "shah" },
            { word: "table", translation: "table", pronunciation: "tah-bluh" },
          ],
        },
        {
          letter: "E",
          name: "e",
          sound: "Varies: 'uh' (schwa), 'eh', or silent",
          examples: [
            { word: "le", translation: "the", pronunciation: "luh" },
            { word: "merci", translation: "thank you", pronunciation: "mehr-SEE" },
          ],
          notes: "Often silent at the end of words",
        },
        {
          letter: "I",
          name: "i",
          sound: "'ee' as in 'machine'",
          examples: [
            { word: "ici", translation: "here", pronunciation: "ee-SEE" },
            { word: "livre", translation: "book", pronunciation: "LEE-vruh" },
          ],
        },
        {
          letter: "O",
          name: "o",
          sound: "'oh' as in 'go' or 'aw' as in 'law'",
          examples: [
            { word: "mot", translation: "word", pronunciation: "moh" },
            { word: "bonne", translation: "good (f)", pronunciation: "bohn" },
          ],
        },
        {
          letter: "U",
          name: "u",
          sound: "Rounded 'ee' - purse lips and say 'ee'",
          examples: [
            { word: "tu", translation: "you", pronunciation: "tew" },
            { word: "lune", translation: "moon", pronunciation: "lewn" },
          ],
          notes: "This sound doesn't exist in English",
        },
      ],
    },
    {
      title: "Nasal Vowels",
      description: "Vowels pronounced through the nose",
      letters: [
        {
          letter: "AN/EN",
          name: "a nasal",
          sound: "Nasal 'ah' - air through nose",
          examples: [
            { word: "enfant", translation: "child", pronunciation: "ahn-FAHN" },
            { word: "dans", translation: "in", pronunciation: "dahn" },
          ],
        },
        {
          letter: "ON",
          name: "o nasal",
          sound: "Nasal 'oh'",
          examples: [
            { word: "bon", translation: "good", pronunciation: "bohn" },
            { word: "maison", translation: "house", pronunciation: "meh-ZOHN" },
          ],
        },
        {
          letter: "IN/AIN",
          name: "i nasal",
          sound: "Nasal 'eh'",
          examples: [
            { word: "vin", translation: "wine", pronunciation: "vahn" },
            { word: "pain", translation: "bread", pronunciation: "pahn" },
          ],
        },
      ],
    },
    {
      title: "Special Letters",
      description: "Accented letters and digraphs",
      letters: [
        {
          letter: "C/Ç",
          name: "c cédille",
          sound: "'s' before a, o, u when written ç",
          examples: [
            { word: "français", translation: "French", pronunciation: "frahn-SEH" },
            { word: "garçon", translation: "boy", pronunciation: "gahr-SOHN" },
          ],
        },
        {
          letter: "É",
          name: "e accent aigu",
          sound: "'ay' as in 'day'",
          examples: [
            { word: "été", translation: "summer", pronunciation: "ay-TAY" },
            { word: "café", translation: "coffee", pronunciation: "kah-FAY" },
          ],
        },
        {
          letter: "È/Ê",
          name: "e accent grave/circonflexe",
          sound: "'eh' as in 'bed'",
          examples: [
            { word: "mère", translation: "mother", pronunciation: "mehr" },
            { word: "tête", translation: "head", pronunciation: "teht" },
          ],
        },
      ],
    },
  ],
};

// German alphabet
const germanAlphabet: LanguageAlphabet = {
  language: "German",
  sections: [
    {
      title: "Vowels",
      description: "German vowels can be short or long",
      letters: [
        {
          letter: "A",
          name: "a",
          sound: "'ah' as in 'father'",
          examples: [
            { word: "Vater", translation: "father", pronunciation: "FAH-ter" },
            { word: "Mann", translation: "man", pronunciation: "mahn" },
          ],
        },
        {
          letter: "E",
          name: "e",
          sound: "'eh' as in 'bed' or 'ay' when long",
          examples: [
            { word: "Bett", translation: "bed", pronunciation: "bet" },
            { word: "See", translation: "lake", pronunciation: "zay" },
          ],
        },
        {
          letter: "I",
          name: "i",
          sound: "'ih' as in 'bit' or 'ee' when long",
          examples: [
            { word: "bin", translation: "am", pronunciation: "bin" },
            { word: "Kino", translation: "cinema", pronunciation: "KEE-noh" },
          ],
        },
        {
          letter: "O",
          name: "o",
          sound: "'oh' as in 'go'",
          examples: [
            { word: "Boot", translation: "boat", pronunciation: "boht" },
            { word: "Sonne", translation: "sun", pronunciation: "ZON-nuh" },
          ],
        },
        {
          letter: "U",
          name: "u",
          sound: "'oo' as in 'moon'",
          examples: [
            { word: "Buch", translation: "book", pronunciation: "bookh" },
            { word: "Mutter", translation: "mother", pronunciation: "MOO-ter" },
          ],
        },
      ],
    },
    {
      title: "Umlauts",
      description: "Modified vowels unique to German",
      letters: [
        {
          letter: "Ä",
          name: "a-Umlaut",
          sound: "'eh' as in 'bed'",
          examples: [
            { word: "Mädchen", translation: "girl", pronunciation: "MEHD-khen" },
            { word: "Käse", translation: "cheese", pronunciation: "KAY-zuh" },
          ],
        },
        {
          letter: "Ö",
          name: "o-Umlaut",
          sound: "Like 'er' in 'her' but rounded",
          examples: [
            { word: "schön", translation: "beautiful", pronunciation: "shern" },
            { word: "Öl", translation: "oil", pronunciation: "erl" },
          ],
        },
        {
          letter: "Ü",
          name: "u-Umlaut",
          sound: "Rounded 'ee' (like French 'u')",
          examples: [
            { word: "über", translation: "over", pronunciation: "EW-ber" },
            { word: "Tür", translation: "door", pronunciation: "tewr" },
          ],
        },
      ],
    },
    {
      title: "Special Consonants",
      description: "Consonants that differ from English",
      letters: [
        {
          letter: "ß",
          name: "Eszett/scharfes S",
          sound: "'ss' sound",
          examples: [
            { word: "Straße", translation: "street", pronunciation: "SHTRAH-suh" },
            { word: "groß", translation: "big", pronunciation: "grohs" },
          ],
          notes: "Only used in German, represents a sharp 's' sound",
        },
        {
          letter: "CH",
          name: "ch",
          sound: "Soft 'kh' (after e, i) or hard 'ch' (after a, o, u)",
          examples: [
            { word: "ich", translation: "I", pronunciation: "ikh" },
            { word: "Buch", translation: "book", pronunciation: "bookh" },
          ],
        },
        {
          letter: "W",
          name: "we",
          sound: "'v' as in 'very'",
          examples: [
            { word: "Wasser", translation: "water", pronunciation: "VAH-ser" },
            { word: "Wein", translation: "wine", pronunciation: "vine" },
          ],
        },
        {
          letter: "Z",
          name: "zet",
          sound: "'ts' as in 'cats'",
          examples: [
            { word: "Zeit", translation: "time", pronunciation: "tsait" },
            { word: "Zimmer", translation: "room", pronunciation: "TSIM-mer" },
          ],
        },
      ],
    },
  ],
};

// Italian alphabet
const italianAlphabet: LanguageAlphabet = {
  language: "Italian",
  sections: [
    {
      title: "Vowels",
      description: "Italian has 7 vowel sounds - all pure, no diphthongs",
      letters: [
        {
          letter: "A",
          name: "a",
          sound: "'ah' as in 'father'",
          examples: [
            { word: "casa", translation: "house", pronunciation: "KAH-zah" },
            { word: "pasta", translation: "pasta", pronunciation: "PAH-stah" },
          ],
        },
        {
          letter: "E",
          name: "e",
          sound: "Open 'eh' or closed 'ay'",
          examples: [
            { word: "bene", translation: "well", pronunciation: "BEH-neh" },
            { word: "verde", translation: "green", pronunciation: "VEHR-deh" },
          ],
        },
        {
          letter: "I",
          name: "i",
          sound: "'ee' as in 'machine'",
          examples: [
            { word: "vino", translation: "wine", pronunciation: "VEE-noh" },
            { word: "pizza", translation: "pizza", pronunciation: "PEET-tsah" },
          ],
        },
        {
          letter: "O",
          name: "o",
          sound: "Open 'aw' or closed 'oh'",
          examples: [
            { word: "buono", translation: "good", pronunciation: "BWOH-noh" },
            { word: "sono", translation: "I am", pronunciation: "SOH-noh" },
          ],
        },
        {
          letter: "U",
          name: "u",
          sound: "'oo' as in 'moon'",
          examples: [
            { word: "uno", translation: "one", pronunciation: "OO-noh" },
            { word: "luna", translation: "moon", pronunciation: "LOO-nah" },
          ],
        },
      ],
    },
    {
      title: "Special Consonants",
      description: "Consonant combinations unique to Italian",
      letters: [
        {
          letter: "C",
          name: "ci",
          sound: "'k' before a/o/u, 'ch' before e/i",
          examples: [
            { word: "casa", translation: "house", pronunciation: "KAH-zah" },
            { word: "ciao", translation: "hello/bye", pronunciation: "CHOW" },
          ],
        },
        {
          letter: "G",
          name: "gi",
          sound: "'g' before a/o/u, 'j' before e/i",
          examples: [
            { word: "gatto", translation: "cat", pronunciation: "GAHT-toh" },
            { word: "gelato", translation: "ice cream", pronunciation: "jeh-LAH-toh" },
          ],
        },
        {
          letter: "GL",
          name: "gli",
          sound: "'ly' as in 'million'",
          examples: [
            { word: "famiglia", translation: "family", pronunciation: "fah-MEE-lyah" },
            { word: "gli", translation: "the (m.pl.)", pronunciation: "lyee" },
          ],
        },
        {
          letter: "GN",
          name: "gn",
          sound: "'ny' as in 'canyon'",
          examples: [
            { word: "gnocchi", translation: "gnocchi", pronunciation: "NYOH-kee" },
            { word: "bagno", translation: "bathroom", pronunciation: "BAH-nyoh" },
          ],
        },
        {
          letter: "SC",
          name: "sc",
          sound: "'sk' before a/o/u, 'sh' before e/i",
          examples: [
            { word: "scuola", translation: "school", pronunciation: "SKWOH-lah" },
            { word: "pesce", translation: "fish", pronunciation: "PEH-sheh" },
          ],
        },
      ],
    },
    {
      title: "Double Consonants",
      description: "Doubled consonants are pronounced longer",
      letters: [
        {
          letter: "Double Letters",
          name: "doppie",
          sound: "Hold the consonant longer",
          examples: [
            { word: "pizza", translation: "pizza", pronunciation: "PEET-tsah" },
            { word: "spaghetti", translation: "spaghetti", pronunciation: "spah-GEHT-tee" },
            { word: "anno", translation: "year", pronunciation: "AHN-noh" },
          ],
          notes: "The difference between single and double consonants can change meaning: pala (shovel) vs palla (ball)",
        },
      ],
    },
  ],
};

// Japanese (Hiragana basics)
const japaneseAlphabet: LanguageAlphabet = {
  language: "Japanese",
  sections: [
    {
      title: "Basic Vowels (Hiragana)",
      description: "Japanese has 5 pure vowel sounds",
      letters: [
        {
          letter: "あ (a)",
          name: "a",
          sound: "'ah' as in 'father'",
          examples: [
            { word: "あさ", translation: "morning", pronunciation: "a-sa" },
            { word: "あめ", translation: "rain", pronunciation: "a-me" },
          ],
        },
        {
          letter: "い (i)",
          name: "i",
          sound: "'ee' as in 'see'",
          examples: [
            { word: "いぬ", translation: "dog", pronunciation: "i-nu" },
            { word: "いえ", translation: "house", pronunciation: "i-e" },
          ],
        },
        {
          letter: "う (u)",
          name: "u",
          sound: "'oo' as in 'food' but shorter",
          examples: [
            { word: "うみ", translation: "sea", pronunciation: "u-mi" },
            { word: "うた", translation: "song", pronunciation: "u-ta" },
          ],
        },
        {
          letter: "え (e)",
          name: "e",
          sound: "'eh' as in 'bed'",
          examples: [
            { word: "えき", translation: "station", pronunciation: "e-ki" },
            { word: "えん", translation: "yen", pronunciation: "en" },
          ],
        },
        {
          letter: "お (o)",
          name: "o",
          sound: "'oh' as in 'go'",
          examples: [
            { word: "おちゃ", translation: "tea", pronunciation: "o-cha" },
            { word: "おと", translation: "sound", pronunciation: "o-to" },
          ],
        },
      ],
    },
    {
      title: "K-Row (か行)",
      description: "Consonant K + vowels",
      letters: [
        {
          letter: "か き く け こ",
          name: "ka ki ku ke ko",
          sound: "'k' + vowel",
          examples: [
            { word: "かわ", translation: "river", pronunciation: "ka-wa" },
            { word: "きく", translation: "to listen", pronunciation: "ki-ku" },
            { word: "くも", translation: "cloud", pronunciation: "ku-mo" },
          ],
        },
      ],
    },
    {
      title: "Special Sounds",
      description: "Unique Japanese sounds",
      letters: [
        {
          letter: "っ (small tsu)",
          name: "sokuon",
          sound: "Doubles the following consonant (pause)",
          examples: [
            { word: "がっこう", translation: "school", pronunciation: "gak-kou" },
            { word: "きって", translation: "stamp", pronunciation: "kit-te" },
          ],
        },
        {
          letter: "ん (n)",
          name: "n",
          sound: "Nasal 'n' or 'm' depending on context",
          examples: [
            { word: "さん", translation: "three", pronunciation: "san" },
            { word: "せんせい", translation: "teacher", pronunciation: "sen-sei" },
          ],
        },
      ],
    },
  ],
};

// Korean (Hangul basics)
const koreanAlphabet: LanguageAlphabet = {
  language: "Korean",
  sections: [
    {
      title: "Basic Vowels",
      description: "Korean has 10 basic vowels",
      letters: [
        {
          letter: "ㅏ (a)",
          name: "a",
          sound: "'ah' as in 'father'",
          examples: [
            { word: "아버지", translation: "father", pronunciation: "a-beo-ji" },
          ],
        },
        {
          letter: "ㅓ (eo)",
          name: "eo",
          sound: "'uh' as in 'cup'",
          examples: [
            { word: "어머니", translation: "mother", pronunciation: "eo-meo-ni" },
          ],
        },
        {
          letter: "ㅗ (o)",
          name: "o",
          sound: "'oh' as in 'go'",
          examples: [
            { word: "오빠", translation: "older brother (f)", pronunciation: "o-ppa" },
          ],
        },
        {
          letter: "ㅜ (u)",
          name: "u",
          sound: "'oo' as in 'food'",
          examples: [
            { word: "우유", translation: "milk", pronunciation: "u-yu" },
          ],
        },
        {
          letter: "ㅡ (eu)",
          name: "eu",
          sound: "No English equivalent - unrounded 'oo'",
          examples: [
            { word: "그", translation: "he/that", pronunciation: "geu" },
          ],
          notes: "Keep lips unrounded while making an 'oo' sound",
        },
        {
          letter: "ㅣ (i)",
          name: "i",
          sound: "'ee' as in 'see'",
          examples: [
            { word: "이", translation: "this/two", pronunciation: "i" },
          ],
        },
      ],
    },
    {
      title: "Basic Consonants",
      description: "Korean has 14 basic consonants",
      letters: [
        {
          letter: "ㄱ (g/k)",
          name: "giyeok",
          sound: "'g' at start, 'k' at end",
          examples: [
            { word: "가다", translation: "to go", pronunciation: "ga-da" },
            { word: "학교", translation: "school", pronunciation: "hak-gyo" },
          ],
        },
        {
          letter: "ㄴ (n)",
          name: "nieun",
          sound: "'n' as in 'no'",
          examples: [
            { word: "나", translation: "I/me", pronunciation: "na" },
          ],
        },
        {
          letter: "ㄷ (d/t)",
          name: "digeut",
          sound: "'d' at start, 't' at end",
          examples: [
            { word: "다", translation: "all", pronunciation: "da" },
          ],
        },
        {
          letter: "ㅁ (m)",
          name: "mieum",
          sound: "'m' as in 'mom'",
          examples: [
            { word: "물", translation: "water", pronunciation: "mul" },
          ],
        },
        {
          letter: "ㅂ (b/p)",
          name: "bieup",
          sound: "'b' at start, 'p' at end",
          examples: [
            { word: "밥", translation: "rice/meal", pronunciation: "bap" },
          ],
        },
      ],
    },
  ],
};

// Simplified Chinese (Pinyin basics)
const chineseAlphabet: LanguageAlphabet = {
  language: "Chinese",
  sections: [
    {
      title: "Tones",
      description: "Mandarin has 4 tones plus a neutral tone",
      letters: [
        {
          letter: "1st Tone (ˉ)",
          name: "high level",
          sound: "High, flat pitch - like singing a high note",
          examples: [
            { word: "妈 (mā)", translation: "mother", pronunciation: "mah (high flat)" },
          ],
        },
        {
          letter: "2nd Tone (ˊ)",
          name: "rising",
          sound: "Rising pitch - like asking 'What?'",
          examples: [
            { word: "麻 (má)", translation: "hemp", pronunciation: "mah (rising)" },
          ],
        },
        {
          letter: "3rd Tone (ˇ)",
          name: "falling-rising",
          sound: "Dips down then rises",
          examples: [
            { word: "马 (mǎ)", translation: "horse", pronunciation: "mah (dip-rise)" },
          ],
        },
        {
          letter: "4th Tone (ˋ)",
          name: "falling",
          sound: "Sharp falling pitch - like saying 'No!'",
          examples: [
            { word: "骂 (mà)", translation: "scold", pronunciation: "mah (falling)" },
          ],
        },
      ],
    },
    {
      title: "Basic Vowels (Pinyin)",
      description: "Chinese vowel sounds in Pinyin romanization",
      letters: [
        {
          letter: "a",
          name: "a",
          sound: "'ah' as in 'father'",
          examples: [
            { word: "大 (dà)", translation: "big", pronunciation: "dah" },
          ],
        },
        {
          letter: "e",
          name: "e",
          sound: "'uh' as in 'duh'",
          examples: [
            { word: "喝 (hē)", translation: "drink", pronunciation: "huh" },
          ],
        },
        {
          letter: "i",
          name: "i",
          sound: "'ee' as in 'see'",
          examples: [
            { word: "你 (nǐ)", translation: "you", pronunciation: "nee" },
          ],
        },
        {
          letter: "o",
          name: "o",
          sound: "'oh' as in 'go'",
          examples: [
            { word: "我 (wǒ)", translation: "I/me", pronunciation: "woh" },
          ],
        },
        {
          letter: "u",
          name: "u",
          sound: "'oo' as in 'food'",
          examples: [
            { word: "五 (wǔ)", translation: "five", pronunciation: "woo" },
          ],
        },
        {
          letter: "ü",
          name: "ü",
          sound: "Rounded 'ee' (like German ü)",
          examples: [
            { word: "女 (nǚ)", translation: "woman", pronunciation: "new" },
          ],
          notes: "Purse your lips while saying 'ee'",
        },
      ],
    },
    {
      title: "Special Consonants",
      description: "Consonants that differ from English",
      letters: [
        {
          letter: "zh",
          name: "zh",
          sound: "'j' as in 'judge' but retroflex",
          examples: [
            { word: "中 (zhōng)", translation: "middle/China", pronunciation: "jong" },
          ],
        },
        {
          letter: "ch",
          name: "ch",
          sound: "'ch' as in 'church' but retroflex",
          examples: [
            { word: "吃 (chī)", translation: "eat", pronunciation: "chr" },
          ],
        },
        {
          letter: "sh",
          name: "sh",
          sound: "'sh' as in 'ship' but retroflex",
          examples: [
            { word: "是 (shì)", translation: "is/yes", pronunciation: "shr" },
          ],
        },
        {
          letter: "x",
          name: "x",
          sound: "'sh' but with tongue forward",
          examples: [
            { word: "谢谢 (xièxie)", translation: "thank you", pronunciation: "shyeh-shyeh" },
          ],
        },
        {
          letter: "q",
          name: "q",
          sound: "'ch' but with tongue forward",
          examples: [
            { word: "请 (qǐng)", translation: "please", pronunciation: "ching" },
          ],
        },
      ],
    },
  ],
};

// Hebrew alphabet
const hebrewAlphabet: LanguageAlphabet = {
  language: "Hebrew",
  sections: [
    {
      title: "Consonants (First Half)",
      description: "Hebrew has 22 consonants - vowels are marked with dots/dashes",
      letters: [
        {
          letter: "א",
          name: "Aleph",
          sound: "Silent or glottal stop",
          examples: [
            { word: "אני", translation: "I", pronunciation: "ah-NEE" },
            { word: "אבא", translation: "father", pronunciation: "AH-bah" },
          ],
          notes: "Often silent, serves as a vowel carrier",
        },
        {
          letter: "ב",
          name: "Bet/Vet",
          sound: "'b' with dot (בּ), 'v' without (ב)",
          examples: [
            { word: "בית", translation: "house", pronunciation: "BAH-yit" },
            { word: "טוב", translation: "good", pronunciation: "tohv" },
          ],
        },
        {
          letter: "ג",
          name: "Gimel",
          sound: "'g' as in 'go'",
          examples: [
            { word: "גדול", translation: "big", pronunciation: "gah-DOHL" },
          ],
        },
        {
          letter: "ד",
          name: "Dalet",
          sound: "'d' as in 'day'",
          examples: [
            { word: "דלת", translation: "door", pronunciation: "DEH-let" },
          ],
        },
        {
          letter: "ה",
          name: "He",
          sound: "'h' as in 'hello' or silent at end",
          examples: [
            { word: "הוא", translation: "he", pronunciation: "hoo" },
          ],
        },
        {
          letter: "ו",
          name: "Vav",
          sound: "'v' or vowel marker (o/u)",
          examples: [
            { word: "טוב", translation: "good", pronunciation: "tohv" },
          ],
          notes: "Can represent 'o' or 'u' vowel sounds",
        },
        {
          letter: "ז",
          name: "Zayin",
          sound: "'z' as in 'zoo'",
          examples: [
            { word: "זמן", translation: "time", pronunciation: "zman" },
          ],
        },
        {
          letter: "ח",
          name: "Chet",
          sound: "Throaty 'ch' like German 'Bach'",
          examples: [
            { word: "חלב", translation: "milk", pronunciation: "khah-LAHV" },
          ],
        },
        {
          letter: "ט",
          name: "Tet",
          sound: "'t' as in 'top'",
          examples: [
            { word: "טוב", translation: "good", pronunciation: "tohv" },
          ],
        },
        {
          letter: "י",
          name: "Yod",
          sound: "'y' as in 'yes' or vowel marker (i)",
          examples: [
            { word: "יום", translation: "day", pronunciation: "yohm" },
          ],
          notes: "Smallest letter, can mark 'i' vowel",
        },
        {
          letter: "כ",
          name: "Kaf/Khaf",
          sound: "'k' with dot (כּ), 'kh' without (כ)",
          examples: [
            { word: "כלב", translation: "dog", pronunciation: "KEH-lev" },
          ],
        },
      ],
    },
    {
      title: "Consonants (Second Half)",
      description: "Continuing the Hebrew consonants",
      letters: [
        {
          letter: "ל",
          name: "Lamed",
          sound: "'l' as in 'love'",
          examples: [
            { word: "לילה", translation: "night", pronunciation: "LYE-lah" },
          ],
        },
        {
          letter: "מ",
          name: "Mem",
          sound: "'m' as in 'mom'",
          examples: [
            { word: "מים", translation: "water", pronunciation: "MAH-yim" },
          ],
          notes: "Has a final form: ם",
        },
        {
          letter: "נ",
          name: "Nun",
          sound: "'n' as in 'no'",
          examples: [
            { word: "נר", translation: "candle", pronunciation: "nehr" },
          ],
          notes: "Has a final form: ן",
        },
        {
          letter: "ס",
          name: "Samech",
          sound: "'s' as in 'sun'",
          examples: [
            { word: "סוס", translation: "horse", pronunciation: "soos" },
          ],
        },
        {
          letter: "ע",
          name: "Ayin",
          sound: "Originally guttural, now often silent",
          examples: [
            { word: "עין", translation: "eye", pronunciation: "AH-yin" },
          ],
          notes: "In modern Israeli Hebrew, often like aleph",
        },
        {
          letter: "פ",
          name: "Pe/Fe",
          sound: "'p' with dot (פּ), 'f' without (פ)",
          examples: [
            { word: "פה", translation: "here/mouth", pronunciation: "poh" },
          ],
          notes: "Has a final form: ף",
        },
        {
          letter: "צ",
          name: "Tsadi",
          sound: "'ts' as in 'cats'",
          examples: [
            { word: "צהריים", translation: "noon", pronunciation: "tsoh-hoh-RAH-yim" },
          ],
          notes: "Has a final form: ץ",
        },
        {
          letter: "ק",
          name: "Qof",
          sound: "'k' as in 'kite'",
          examples: [
            { word: "קטן", translation: "small", pronunciation: "kah-TAHN" },
          ],
        },
        {
          letter: "ר",
          name: "Resh",
          sound: "Guttural 'r' (like French 'r')",
          examples: [
            { word: "רחוב", translation: "street", pronunciation: "reh-KHOHV" },
          ],
        },
        {
          letter: "ש",
          name: "Shin/Sin",
          sound: "'sh' with right dot (שׁ), 's' with left dot (שׂ)",
          examples: [
            { word: "שלום", translation: "peace/hello", pronunciation: "shah-LOHM" },
          ],
        },
        {
          letter: "ת",
          name: "Tav",
          sound: "'t' as in 'top'",
          examples: [
            { word: "תודה", translation: "thank you", pronunciation: "toh-DAH" },
          ],
        },
      ],
    },
    {
      title: "Final Forms",
      description: "Five letters have special forms when at the end of a word",
      letters: [
        {
          letter: "ך",
          name: "Final Kaf",
          sound: "Always 'kh' at word end",
          examples: [
            { word: "לך", translation: "to you", pronunciation: "leh-KHAH" },
          ],
        },
        {
          letter: "ם",
          name: "Final Mem",
          sound: "'m' at word end",
          examples: [
            { word: "מים", translation: "water", pronunciation: "MAH-yim" },
          ],
        },
        {
          letter: "ן",
          name: "Final Nun",
          sound: "'n' at word end",
          examples: [
            { word: "כן", translation: "yes", pronunciation: "kehn" },
          ],
        },
        {
          letter: "ף",
          name: "Final Pe",
          sound: "Always 'f' at word end",
          examples: [
            { word: "כסף", translation: "money/silver", pronunciation: "KEH-sef" },
          ],
        },
        {
          letter: "ץ",
          name: "Final Tsadi",
          sound: "'ts' at word end",
          examples: [
            { word: "ארץ", translation: "land/country", pronunciation: "EH-rets" },
          ],
        },
      ],
    },
  ],
};

// Russian (Cyrillic)
const russianAlphabet: LanguageAlphabet = {
  language: "Russian",
  sections: [
    {
      title: "Vowels",
      description: "Russian has 10 vowel letters, some 'soft' and some 'hard'",
      letters: [
        {
          letter: "А а",
          name: "a",
          sound: "'ah' as in 'father'",
          examples: [
            { word: "мама", translation: "mom", pronunciation: "MAH-mah" },
          ],
        },
        {
          letter: "О о",
          name: "o",
          sound: "'oh' when stressed, 'ah' when unstressed",
          examples: [
            { word: "молоко", translation: "milk", pronunciation: "mah-lah-KOH" },
          ],
          notes: "Vowel reduction: unstressed 'о' sounds like 'а'",
        },
        {
          letter: "У у",
          name: "u",
          sound: "'oo' as in 'food'",
          examples: [
            { word: "утро", translation: "morning", pronunciation: "OO-trah" },
          ],
        },
        {
          letter: "Э э",
          name: "e",
          sound: "'eh' as in 'bed'",
          examples: [
            { word: "это", translation: "this", pronunciation: "EH-tah" },
          ],
        },
        {
          letter: "Ы ы",
          name: "y",
          sound: "No English equivalent - 'ee' with tongue pulled back",
          examples: [
            { word: "мы", translation: "we", pronunciation: "mih" },
          ],
          notes: "Unique Russian sound, between 'ee' and 'uh'",
        },
        {
          letter: "И и",
          name: "i",
          sound: "'ee' as in 'see'",
          examples: [
            { word: "или", translation: "or", pronunciation: "EE-lee" },
          ],
        },
      ],
    },
    {
      title: "Soft Vowels",
      description: "These vowels soften (palatalize) the preceding consonant",
      letters: [
        {
          letter: "Я я",
          name: "ya",
          sound: "'ya' as in 'yard'",
          examples: [
            { word: "яблоко", translation: "apple", pronunciation: "YAH-blah-kah" },
          ],
        },
        {
          letter: "Ё ё",
          name: "yo",
          sound: "'yo' as in 'yolk'",
          examples: [
            { word: "ёлка", translation: "Christmas tree", pronunciation: "YOHL-kah" },
          ],
          notes: "Always stressed; often written as 'е' in casual text",
        },
        {
          letter: "Ю ю",
          name: "yu",
          sound: "'you'",
          examples: [
            { word: "юг", translation: "south", pronunciation: "yook" },
          ],
        },
        {
          letter: "Е е",
          name: "ye",
          sound: "'ye' as in 'yes'",
          examples: [
            { word: "есть", translation: "to eat/there is", pronunciation: "yest" },
          ],
        },
      ],
    },
    {
      title: "Common Consonants",
      description: "Some consonants that differ from English",
      letters: [
        {
          letter: "Ж ж",
          name: "zhe",
          sound: "'zh' as in 'measure'",
          examples: [
            { word: "жена", translation: "wife", pronunciation: "zheh-NAH" },
          ],
        },
        {
          letter: "Ц ц",
          name: "tse",
          sound: "'ts' as in 'cats'",
          examples: [
            { word: "цена", translation: "price", pronunciation: "tseh-NAH" },
          ],
        },
        {
          letter: "Ч ч",
          name: "che",
          sound: "'ch' as in 'cheese'",
          examples: [
            { word: "чай", translation: "tea", pronunciation: "chay" },
          ],
        },
        {
          letter: "Ш ш",
          name: "sha",
          sound: "'sh' as in 'ship'",
          examples: [
            { word: "школа", translation: "school", pronunciation: "SHKOH-lah" },
          ],
        },
        {
          letter: "Щ щ",
          name: "shcha",
          sound: "Long soft 'sh' - like 'fresh cheese'",
          examples: [
            { word: "щи", translation: "cabbage soup", pronunciation: "shchee" },
          ],
        },
      ],
    },
    {
      title: "Special Signs",
      description: "Letters that modify pronunciation",
      letters: [
        {
          letter: "Ь ь",
          name: "soft sign",
          sound: "Makes preceding consonant soft (palatalized)",
          examples: [
            { word: "мать", translation: "mother", pronunciation: "maht'" },
          ],
          notes: "No sound of its own, just softens the consonant before it",
        },
        {
          letter: "Ъ ъ",
          name: "hard sign",
          sound: "Separates consonant from soft vowel",
          examples: [
            { word: "объект", translation: "object", pronunciation: "ahb-YEKT" },
          ],
          notes: "Rare; prevents palatalization before soft vowels",
        },
      ],
    },
  ],
};

// Polish alphabet
const polishAlphabet: LanguageAlphabet = {
  language: "Polish",
  sections: [
    {
      title: "Vowels",
      description: "Polish has 8 vowel sounds including 2 nasal vowels",
      letters: [
        {
          letter: "A a",
          name: "a",
          sound: "'ah' as in 'father'",
          examples: [
            { word: "tata", translation: "dad", pronunciation: "TAH-tah" },
          ],
        },
        {
          letter: "E e",
          name: "e",
          sound: "'eh' as in 'bed'",
          examples: [
            { word: "bez", translation: "without", pronunciation: "behs" },
          ],
        },
        {
          letter: "I i",
          name: "i",
          sound: "'ee' as in 'see'",
          examples: [
            { word: "i", translation: "and", pronunciation: "ee" },
          ],
        },
        {
          letter: "O o",
          name: "o",
          sound: "'oh' as in 'go'",
          examples: [
            { word: "oko", translation: "eye", pronunciation: "OH-koh" },
          ],
        },
        {
          letter: "U u / Ó ó",
          name: "u",
          sound: "'oo' as in 'food' (both letters same sound)",
          examples: [
            { word: "mur", translation: "wall", pronunciation: "moor" },
            { word: "mój", translation: "my", pronunciation: "mooy" },
          ],
        },
        {
          letter: "Y y",
          name: "y",
          sound: "Like 'i' in 'bit'",
          examples: [
            { word: "syn", translation: "son", pronunciation: "sihn" },
          ],
        },
      ],
    },
    {
      title: "Nasal Vowels",
      description: "Unique to Polish among Slavic languages",
      letters: [
        {
          letter: "Ą ą",
          name: "a ogonek",
          sound: "Nasal 'oh' - like French 'on'",
          examples: [
            { word: "są", translation: "they are", pronunciation: "sohN" },
          ],
        },
        {
          letter: "Ę ę",
          name: "e ogonek",
          sound: "Nasal 'eh' - like French 'in'",
          examples: [
            { word: "będę", translation: "I will be", pronunciation: "BEN-deh" },
          ],
          notes: "At word end, often just 'e' in casual speech",
        },
      ],
    },
    {
      title: "Special Consonants",
      description: "Polish has many consonant combinations",
      letters: [
        {
          letter: "Ć ć / CI ci",
          name: "ci",
          sound: "Soft 'ch' - like 'cheese' but softer",
          examples: [
            { word: "ćma", translation: "moth", pronunciation: "chmah" },
          ],
        },
        {
          letter: "Ś ś / SI si",
          name: "si",
          sound: "Soft 'sh' - tongue touches palate",
          examples: [
            { word: "śnieg", translation: "snow", pronunciation: "shnyehk" },
          ],
        },
        {
          letter: "Ź ź / ZI zi",
          name: "zi",
          sound: "Soft 'zh' - like 'vision' but softer",
          examples: [
            { word: "źle", translation: "badly", pronunciation: "zhleh" },
          ],
        },
        {
          letter: "Ń ń / NI ni",
          name: "ni",
          sound: "'ny' as in 'canyon'",
          examples: [
            { word: "koń", translation: "horse", pronunciation: "kohn-y" },
          ],
        },
        {
          letter: "Ż ż / RZ rz",
          name: "zet",
          sound: "'zh' as in 'measure' (both same sound)",
          examples: [
            { word: "żaba", translation: "frog", pronunciation: "ZHAH-bah" },
            { word: "rzeka", translation: "river", pronunciation: "ZHEH-kah" },
          ],
        },
        {
          letter: "Sz sz",
          name: "sz",
          sound: "'sh' as in 'ship'",
          examples: [
            { word: "szkoła", translation: "school", pronunciation: "SHKOH-wah" },
          ],
        },
        {
          letter: "Cz cz",
          name: "cz",
          sound: "'ch' as in 'cheese'",
          examples: [
            { word: "czas", translation: "time", pronunciation: "chahs" },
          ],
        },
        {
          letter: "Ł ł",
          name: "w",
          sound: "'w' as in 'water'",
          examples: [
            { word: "łódka", translation: "boat", pronunciation: "WOOT-kah" },
          ],
          notes: "Not 'l' - sounds like English 'w'",
        },
        {
          letter: "W w",
          name: "v",
          sound: "'v' as in 'very'",
          examples: [
            { word: "woda", translation: "water", pronunciation: "VOH-dah" },
          ],
        },
      ],
    },
  ],
};

// Ukrainian (Cyrillic)
const ukrainianAlphabet: LanguageAlphabet = {
  language: "Ukrainian",
  sections: [
    {
      title: "Vowels",
      description: "Ukrainian has 6 vowel letters",
      letters: [
        {
          letter: "А а",
          name: "a",
          sound: "'ah' as in 'father'",
          examples: [
            { word: "мама", translation: "mom", pronunciation: "MAH-mah" },
          ],
        },
        {
          letter: "О о",
          name: "o",
          sound: "'oh' as in 'go' (doesn't reduce like Russian)",
          examples: [
            { word: "молоко", translation: "milk", pronunciation: "moh-loh-KOH" },
          ],
        },
        {
          letter: "У у",
          name: "u",
          sound: "'oo' as in 'food'",
          examples: [
            { word: "улюблений", translation: "favorite", pronunciation: "oo-lyub-LEH-niy" },
          ],
        },
        {
          letter: "Е е",
          name: "e",
          sound: "'eh' as in 'bed'",
          examples: [
            { word: "день", translation: "day", pronunciation: "dehn" },
          ],
        },
        {
          letter: "И и",
          name: "y",
          sound: "Between 'i' and 'y' - like 'i' in 'bit'",
          examples: [
            { word: "ми", translation: "we", pronunciation: "mih" },
          ],
        },
        {
          letter: "І і",
          name: "i",
          sound: "'ee' as in 'see'",
          examples: [
            { word: "і", translation: "and", pronunciation: "ee" },
          ],
        },
      ],
    },
    {
      title: "Soft Vowels",
      description: "These vowels palatalize preceding consonants",
      letters: [
        {
          letter: "Я я",
          name: "ya",
          sound: "'ya' as in 'yard'",
          examples: [
            { word: "яблуко", translation: "apple", pronunciation: "YAH-bloo-koh" },
          ],
        },
        {
          letter: "Ю ю",
          name: "yu",
          sound: "'you'",
          examples: [
            { word: "юнак", translation: "young man", pronunciation: "yoo-NAHK" },
          ],
        },
        {
          letter: "Є є",
          name: "ye",
          sound: "'ye' as in 'yes'",
          examples: [
            { word: "Європа", translation: "Europe", pronunciation: "yev-ROH-pah" },
          ],
        },
        {
          letter: "Ї ї",
          name: "yi",
          sound: "'yee'",
          examples: [
            { word: "їжа", translation: "food", pronunciation: "YEE-zhah" },
          ],
          notes: "Unique to Ukrainian - always 'yi' sound",
        },
      ],
    },
    {
      title: "Special Consonants",
      description: "Ukrainian-specific letters and sounds",
      letters: [
        {
          letter: "Г г",
          name: "h",
          sound: "'h' as in 'hello' (not 'g' like Russian)",
          examples: [
            { word: "голова", translation: "head", pronunciation: "hoh-loh-VAH" },
          ],
          notes: "Unlike Russian, Ukrainian Г is 'h', not 'g'",
        },
        {
          letter: "Ґ ґ",
          name: "g",
          sound: "'g' as in 'go'",
          examples: [
            { word: "ґанок", translation: "porch", pronunciation: "GAH-nok" },
          ],
          notes: "Rare letter for 'g' sound, mostly in loanwords",
        },
        {
          letter: "Ж ж",
          name: "zh",
          sound: "'zh' as in 'measure'",
          examples: [
            { word: "жінка", translation: "woman", pronunciation: "ZHEEN-kah" },
          ],
        },
        {
          letter: "Ч ч",
          name: "ch",
          sound: "'ch' as in 'cheese'",
          examples: [
            { word: "час", translation: "time", pronunciation: "chahs" },
          ],
        },
        {
          letter: "Ш ш",
          name: "sh",
          sound: "'sh' as in 'ship'",
          examples: [
            { word: "школа", translation: "school", pronunciation: "SHKOH-lah" },
          ],
        },
        {
          letter: "Щ щ",
          name: "shch",
          sound: "'shch' - like 'fresh cheese'",
          examples: [
            { word: "ще", translation: "still/yet", pronunciation: "shcheh" },
          ],
        },
        {
          letter: "Ц ц",
          name: "ts",
          sound: "'ts' as in 'cats'",
          examples: [
            { word: "цікаво", translation: "interesting", pronunciation: "tsee-KAH-voh" },
          ],
        },
      ],
    },
    {
      title: "Special Signs",
      description: "Modifying letters",
      letters: [
        {
          letter: "Ь ь",
          name: "soft sign",
          sound: "Softens preceding consonant",
          examples: [
            { word: "сіль", translation: "salt", pronunciation: "seel'" },
          ],
        },
        {
          letter: "' (apostrophe)",
          name: "apostrophe",
          sound: "Separates consonant from soft vowel",
          examples: [
            { word: "м'ясо", translation: "meat", pronunciation: "MYAH-soh" },
          ],
          notes: "Used instead of Russian hard sign (ъ)",
        },
      ],
    },
  ],
};

// Greek alphabet
const greekAlphabet: LanguageAlphabet = {
  language: "Greek",
  sections: [
    {
      title: "Vowels",
      description: "Greek has 5 vowel sounds, though several letters/combinations produce the same sound",
      letters: [
        {
          letter: "Α α",
          name: "alpha",
          sound: "Open 'ah' as in 'father'",
          examples: [
            { word: "αγάπη", translation: "love", pronunciation: "ah-GAH-pee" },
            { word: "άνθρωπος", translation: "person", pronunciation: "AHN-throh-pos" },
          ],
        },
        {
          letter: "Ε ε",
          name: "epsilon",
          sound: "'eh' as in 'bed'",
          examples: [
            { word: "ελιά", translation: "olive", pronunciation: "eh-LYAH" },
            { word: "ένα", translation: "one", pronunciation: "EH-nah" },
          ],
        },
        {
          letter: "Η η",
          name: "eta",
          sound: "'ee' as in 'machine'",
          examples: [
            { word: "ήλιος", translation: "sun", pronunciation: "EE-lee-os" },
          ],
          notes: "Same sound as Ι ι in modern Greek",
        },
        {
          letter: "Ι ι",
          name: "iota",
          sound: "'ee' as in 'machine'",
          examples: [
            { word: "ιδέα", translation: "idea", pronunciation: "ee-DHEH-ah" },
          ],
        },
        {
          letter: "Ο ο",
          name: "omicron",
          sound: "'oh' as in 'go' but shorter",
          examples: [
            { word: "όνομα", translation: "name", pronunciation: "OH-noh-mah" },
          ],
        },
        {
          letter: "Υ υ",
          name: "upsilon",
          sound: "'ee' as in 'machine'",
          examples: [
            { word: "ύπνος", translation: "sleep", pronunciation: "EEP-nos" },
          ],
          notes: "Same sound as Ι ι in modern Greek",
        },
        {
          letter: "Ω ω",
          name: "omega",
          sound: "'oh' as in 'go'",
          examples: [
            { word: "ώρα", translation: "hour/time", pronunciation: "OH-rah" },
          ],
          notes: "Same sound as Ο ο in modern Greek",
        },
      ],
    },
    {
      title: "Common Consonants",
      description: "Many Greek consonants are familiar from English",
      letters: [
        {
          letter: "Β β",
          name: "vita",
          sound: "'v' as in 'vine' (NOT 'b')",
          examples: [
            { word: "βιβλίο", translation: "book", pronunciation: "veev-LEE-oh" },
          ],
          notes: "Unlike ancient Greek, modern β is a 'v' sound",
        },
        {
          letter: "Γ γ",
          name: "gamma",
          sound: "Soft 'gh' — a voiced velar fricative, like a gentle gargle",
          examples: [
            { word: "γάλα", translation: "milk", pronunciation: "GHAH-lah" },
            { word: "γεια", translation: "hello/health", pronunciation: "YAH" },
          ],
          notes: "Before ε/ι sounds, pronounced like 'y' in 'yes'",
        },
        {
          letter: "Δ δ",
          name: "delta",
          sound: "'th' as in 'this' (voiced)",
          examples: [
            { word: "δρόμος", translation: "road", pronunciation: "THROH-mos" },
            { word: "δουλειά", translation: "work", pronunciation: "dhoo-LYAH" },
          ],
        },
        {
          letter: "Θ θ",
          name: "theta",
          sound: "'th' as in 'think' (voiceless)",
          examples: [
            { word: "θάλασσα", translation: "sea", pronunciation: "THAH-lah-sah" },
          ],
        },
        {
          letter: "Κ κ",
          name: "kappa",
          sound: "'k' as in 'key'",
          examples: [
            { word: "καφές", translation: "coffee", pronunciation: "kah-FES" },
          ],
        },
        {
          letter: "Λ λ",
          name: "lambda",
          sound: "'l' as in 'love'",
          examples: [
            { word: "λόγος", translation: "word/reason", pronunciation: "LOH-ghos" },
          ],
        },
        {
          letter: "Μ μ",
          name: "mi",
          sound: "'m' as in 'mother'",
          examples: [
            { word: "μέρα", translation: "day", pronunciation: "MEH-rah" },
          ],
        },
        {
          letter: "Ν ν",
          name: "ni",
          sound: "'n' as in 'no'",
          examples: [
            { word: "νερό", translation: "water", pronunciation: "neh-ROH" },
          ],
        },
        {
          letter: "Π π",
          name: "pi",
          sound: "'p' as in 'pen'",
          examples: [
            { word: "πόλη", translation: "city", pronunciation: "POH-lee" },
          ],
        },
        {
          letter: "Ρ ρ",
          name: "rho",
          sound: "Rolled/trilled 'r'",
          examples: [
            { word: "ρόδο", translation: "rose", pronunciation: "ROH-dhoh" },
          ],
        },
        {
          letter: "Σ σ/ς",
          name: "sigma",
          sound: "'s' as in 'sun'",
          examples: [
            { word: "σπίτι", translation: "house", pronunciation: "SPEE-tee" },
          ],
          notes: "ς is used only at the end of a word",
        },
        {
          letter: "Τ τ",
          name: "taf",
          sound: "'t' as in 'top'",
          examples: [
            { word: "τυρί", translation: "cheese", pronunciation: "tee-REE" },
          ],
        },
        {
          letter: "Χ χ",
          name: "chi",
          sound: "'ch' as in Scottish 'loch' or German 'Bach'",
          examples: [
            { word: "χέρι", translation: "hand", pronunciation: "KHEH-ree" },
          ],
          notes: "Before ε/ι sounds, softer like 'h' in 'huge'",
        },
      ],
    },
    {
      title: "Common Combinations",
      description: "Letter pairs that produce a single sound",
      letters: [
        {
          letter: "ΜΠ μπ",
          name: "mi-pi",
          sound: "'b' as in 'boy'",
          examples: [
            { word: "μπίρα", translation: "beer", pronunciation: "BEE-rah" },
            { word: "μπαμπάς", translation: "dad", pronunciation: "bahm-BAHS" },
          ],
          notes: "This is how Greek writes the 'b' sound",
        },
        {
          letter: "ΝΤ ντ",
          name: "ni-taf",
          sound: "'d' as in 'dog' (or 'nd')",
          examples: [
            { word: "ντομάτα", translation: "tomato", pronunciation: "doh-MAH-tah" },
          ],
          notes: "This is how Greek writes the 'd' sound",
        },
        {
          letter: "ΓΚ γκ",
          name: "gamma-kappa",
          sound: "'g' as in 'go' (or 'ng')",
          examples: [
            { word: "γκαράζ", translation: "garage", pronunciation: "gah-RAHZ" },
          ],
          notes: "This is how Greek writes the hard 'g' sound",
        },
        {
          letter: "ΟΥ ου",
          name: "omicron-upsilon",
          sound: "'oo' as in 'moon'",
          examples: [
            { word: "ουρανός", translation: "sky", pronunciation: "oo-rah-NOS" },
          ],
        },
        {
          letter: "ΑΙ αι",
          name: "alpha-iota",
          sound: "'eh' as in 'bed'",
          examples: [
            { word: "και", translation: "and", pronunciation: "keh" },
          ],
        },
        {
          letter: "ΕΙ ει",
          name: "epsilon-iota",
          sound: "'ee' as in 'machine'",
          examples: [
            { word: "είναι", translation: "is", pronunciation: "EE-neh" },
          ],
        },
        {
          letter: "ΟΙ οι",
          name: "omicron-iota",
          sound: "'ee' as in 'machine'",
          examples: [
            { word: "οικογένεια", translation: "family", pronunciation: "ee-koh-YEH-nee-ah" },
          ],
        },
      ],
    },
  ],
};

// Map language codes to alphabets
export const languageAlphabets: Record<string, LanguageAlphabet> = {
  es: spanishAlphabet,
  fr: frenchAlphabet,
  de: germanAlphabet,
  it: italianAlphabet,
  ja: japaneseAlphabet,
  ko: koreanAlphabet,
  zh: chineseAlphabet,
  he: hebrewAlphabet,
  ru: russianAlphabet,
  pl: polishAlphabet,
  uk: ukrainianAlphabet,
  el: greekAlphabet,
};

